/**
 * Chrome design tokens — **mirrored from dink-admin `src/theme/tokens.ts`**
 * (which in turn mirrors that repo's design handoff verbatim).
 *
 * DO NOT TUNE HERE. These values are a vendored copy carried for as long as the
 * two apps ship separate theme modules; they converge into a shared
 * `@journix/admin-theme` package in the deferred dink-admin pass (SASBND-0008
 * "Execution → out of scope"). Changing a value here without changing it in
 * dink-admin creates silent drift between the two consoles. No new values are
 * invented for the light theme — every literal below exists in dink-admin's
 * `tokens.ts` or its `theme.ts`.
 *
 * Only the subset Brand Center's chrome needs is vendored: dink-admin's
 * membership/tier/avatar maps have no counterpart here.
 *
 * These tokens style **Brand Center's own frame**. The tenant login preview is
 * driven by the tenant's own tokens (`src/features/branding/themeTokens.ts`)
 * and must never read from this module.
 */

// ---- Brand palette (court green) — mode-invariant ----
export const COURT_GREEN = '#2E7D52';
export const COURT_GREEN_DARK = '#266B46';

/** Light-mode page background. */
export const PAGE_BG = '#F4F2EE';

/** Reusable green tints (active nav bg, tonal info surfaces). */
export const GREEN_TINTS = {
  navActive: '#E8F0EA',
  chip: '#E5EFE9',
  surfaceA: '#F3F8F4',
  surfaceB: '#F0F5F1',
} as const;

/**
 * Monospace stack for HTML source readouts, tokens and IDs. `Roboto Mono` is
 * self-hosted via `@fontsource/roboto-mono` (imported in `fonts.ts`).
 */
export const MONO = "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/** UI/body font stack — `Roboto`, self-hosted via `@fontsource/roboto`. */
export const SANS = "'Roboto', system-ui, Arial, sans-serif";

// ---- Shadows (card / dialog / snackbar elevations from the handoff) ----
export const SHADOWS = {
  card: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
  dialog: '0 11px 15px -7px rgba(0,0,0,0.2), 0 24px 38px 3px rgba(0,0,0,0.14)',
  snackbar: '0 6px 16px rgba(0,0,0,0.3)',
  appBar: '0 1px 2px rgba(0,0,0,0.04)',
} as const;

/**
 * Dark elevations — dink-journix's shipped `shadow1` / `shadow2`. The handoff set
 * above is tuned for a light page: `rgba(0,0,0,0.12)` is invisible on `#0B0F0D`,
 * so elevation would read as flat. Borrowed rather than invented, on the same
 * footing as the dark neutrals (SASBND-0008 U2).
 */
const DARK_SHADOWS = {
  card: '0 1px 2px rgba(0,0,0,.45)',
  dialog: '0 16px 40px rgba(0,0,0,.55)',
  appBar: '0 1px 2px rgba(0,0,0,.45)',
} as const;

/**
 * The mode-dependent half of the token set: neutrals, surfaces and the tonal
 * alert palette. Split out from the brand constants above so a second (dark)
 * set can be added in SASBND-0008 U2 with the **same shape** and every
 * component stays mode-agnostic — nothing outside this file reads a raw
 * neutral literal.
 */
export interface ChromeTokens {
  /** MUI palette mode this set describes. */
  mode: 'light' | 'dark';
  /** Page background (`palette.background.default`). */
  pageBg: string;
  /** Card/dialog surface (`palette.background.paper`). */
  surface: string;
  /**
   * Recessed surface for code readouts and preview stages
   * (`palette.background.subtle`) — replaces ad-hoc `grey.50`.
   */
  surfaceSubtle: string;
  /** App bar background. */
  appBarBg: string;
  /** Background of the selected drawer nav item. */
  navActive: string;
  /** Label + icon color of the selected drawer nav item. */
  navActiveFg: string;
  /** Background of a hovered *unselected* drawer nav item. */
  navHover: string;
  /** Background of a hovered *selected* drawer nav item — one step up from `navActive`. */
  navActiveHover: string;
  text: { primary: string; secondary: string; disabled: string };
  /** Standard 1px rule (`palette.divider`). */
  divider: string;
  /** Lighter rule used between dense table rows. */
  dividerSubtle: string;
  /** Outline for unselected toggle buttons. */
  controlBorder: string;
  /**
   * Card / dialog / app-bar elevations. Mode-dependent because a shadow only
   * reads against the surface it falls on — the light set disappears on the dark
   * page background.
   */
  shadows: { card: string; dialog: string; appBar: string };
  /** Tonal alert surfaces, one per severity. */
  alert: Record<
    'error' | 'warning' | 'success' | 'info',
    { bg: string; fg: string; border?: string }
  >;
}

/** Light chrome — the dink-admin light theme, value for value. */
export const LIGHT_CHROME: ChromeTokens = {
  mode: 'light',
  pageBg: PAGE_BG,
  surface: '#FFFFFF',
  // dink-admin's dense table-head surface, reused as the recessed surface.
  surfaceSubtle: '#FBFAF8',
  appBarBg: '#FFFFFF',
  navActive: GREEN_TINTS.navActive,
  // dink-admin's own nav-item sx, value for value: the court green for the
  // selected label/icon, `surfaceB` as the idle hover wash and `chip` — one step
  // stronger than `navActive` — as the selected item's hover.
  navActiveFg: COURT_GREEN,
  navHover: GREEN_TINTS.surfaceB,
  navActiveHover: GREEN_TINTS.chip,
  text: {
    primary: 'rgba(0,0,0,0.87)',
    secondary: 'rgba(0,0,0,0.6)',
    disabled: 'rgba(0,0,0,0.38)',
  },
  divider: 'rgba(0,0,0,0.12)',
  dividerSubtle: 'rgba(0,0,0,0.06)',
  controlBorder: 'rgba(0,0,0,0.18)',
  shadows: { card: SHADOWS.card, dialog: SHADOWS.dialog, appBar: SHADOWS.appBar },
  alert: {
    error: { bg: '#FDECEA', fg: '#9A4A38', border: '1px solid #F5C6BF' },
    warning: { bg: '#FFF7E8', fg: '#7A5510', border: '1px solid #F2DDB0' },
    // dink-admin's `STATUS.active` pair.
    success: { bg: '#E5F1E9', fg: '#1F6B43' },
    info: { bg: GREEN_TINTS.surfaceB, fg: '#2E5E45' },
  },
};

/**
 * Dark chrome (SASBND-0008 U2) — **dink-journix's shipped dark neutral scale**
 * (`src/theme/appTheme.ts`, `buildTokens(_, 'dark')`) with the **court-green
 * brand carried through unchanged**: the identity stays dink-admin's, only the
 * neutrals are borrowed. Like the light set, no values are invented — every
 * literal below is either a dink-journix dark token or a court-green tint at
 * dink-journix's own `brandSoft` alpha.
 *
 * These are the spec's *starting* values and are pending visual QA (spec U2:
 * "these are starting values → visual-QA before merge"). See the delivery's
 * Architect notes for the two judgments that want a design eye — the court green
 * against `#0B0F0D`, and `info` reading blue here but green in light mode.
 */
export const DARK_CHROME: ChromeTokens = {
  mode: 'dark',
  pageBg: '#0B0F0D',
  surface: '#121815',
  // dink-journix's `raise` — in dark, a *raised* step off the surface is what
  // reads as recessed content (a darker well would vanish into the page).
  surfaceSubtle: '#1E2621',
  appBarBg: '#121815',
  // Court green at dink-journix's `brandSoft` alpha (0.16) — the light set's
  // `#E8F0EA` nav tint is opaque and only works over white.
  navActive: 'rgba(46, 125, 82, 0.16)',
  // The light set's nav trio has no opaque dark mirror, so each value is borrowed
  // from dink-journix's shipped dark tokens rather than invented (SASBND-0009 U1):
  //  - `navActiveFg` is its dark `success` green `#3FBF7C` — the same hue as the
  //    court green (hsl 150) lifted to a lightness that reads on `#0B0F0D`. The
  //    court green itself lands near 3.4:1 on the tint and is not a legible label
  //    color here. Flagged for the Operator's visual check.
  //  - `navHover` is `raise`, dink-journix's neutral hover for a ghost control.
  //  - `navActiveHover` is `brandLine` (the brand at alpha 0.34) — dink-journix
  //    ships exactly this as the hover of a `brandSoft` surface, which is what
  //    `navActive` above is.
  navActiveFg: '#3FBF7C',
  navHover: '#1E2621',
  navActiveHover: 'rgba(46, 125, 82, 0.34)',
  text: {
    primary: '#EFF3F0',
    secondary: '#9AA39D',
    // dink-journix's `text3`; the spec names only the first two, but MUI's
    // palette needs all three and this is the same shipped ramp.
    disabled: '#69726C',
  },
  divider: '#283029', // dink-journix `line`
  dividerSubtle: '#1E2621', // `raise` — one step quieter than `line`
  controlBorder: '#3A463E', // `lineStrong`
  shadows: DARK_SHADOWS,
  alert: {
    // dink-journix's dark semantic accents with their `*Soft` surfaces; the
    // error/warning outlines use its `warnLine` alpha (0.45), matching the light
    // set, where those two severities are the outlined ones.
    error: {
      bg: 'rgba(235, 90, 77, 0.16)',
      fg: '#EB5A4D',
      border: '1px solid rgba(235, 90, 77, 0.45)',
    },
    warning: {
      bg: 'rgba(231, 177, 62, 0.18)',
      fg: '#E7B13E',
      border: '1px solid rgba(231, 177, 62, 0.45)',
    },
    success: { bg: 'rgba(63, 191, 124, 0.16)', fg: '#3FBF7C' },
    // Light mode's info pair is a deep green readable on a pale green tint; its
    // dark mirror would need a *light* green, and none exists in either source
    // (the only readable ones are the success greens, which would collide).
    // dink-journix's shipped dark info is used instead rather than inventing a
    // green — flagged for the design call U1 already opened on `palette.info`.
    info: { bg: 'rgba(94, 149, 248, 0.16)', fg: '#5E95F8' },
  },
};

/** Semantic (non-brand) accent colors — dink-admin's `theme.ts` palette. */
export const SEMANTIC = {
  success: { main: '#2E9D5E', dark: '#1F6B43' },
  warning: { main: '#D89A2E', dark: '#8A5A12' },
  error: { main: '#C0492E', dark: '#8C2F1C' },
} as const;
